import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { collection, query, where, onSnapshot } from "firebase/firestore";

const WebDataContext = createContext();

export const WebDataProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // 🔥 ROLES STATE
  const [isAdmin, setIsAdmin] = useState(false); 
  const [isHR, setIsHR] = useState(false);       
  const [isStore, setIsStore] = useState(false); 
  const [isAccount, setIsAccount] = useState(false); 

  // --- DATA STATES ---
  const [users, setUsers] = useState([]); 
  const [tasks, setTasks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [travelNotes, setTravelNotes] = useState([]); 
  const [pms, setPms] = useState([]);
  const [salesReports, setSalesReports] = useState([]);
  const [orders, setOrders] = useState([]);
  const [serviceCalls, setServiceCalls] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [cardRequests, setCardRequests] = useState([]);
  const [activityPlans, setActivityPlans] = useState([]);
  const [spareParts, setSpareParts] = useState([]); 
  const [organizations, setOrganizations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [dues, setDues] = useState([]);
  const [demos, setDemos] = useState([]); 

  // 1. LOGIN & ROLE CHECK LISTENER
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const email = user.email.toLowerCase();
        const name = user.displayName || email.split("@")[0];
        
        // 🔥🔥🔥 ROLE CONFIGURATION (Update Emails Here) 🔥🔥🔥
        const adminEmails = ["varunkhandagre@gmail.com", "admin@lms.com", "varun@mycrm.com"];
        const hrEmails = ["hr@lms.com", "shweta@lms.com"]; 
        const storeEmails = ["store@lms.com", "warehouse@lms.com"];
        const accountEmails = ["account@lms.com", "finance@lms.com", "accountant@lms.com"];

        const checkAdmin = adminEmails.includes(email);
        const checkHR = hrEmails.includes(email);
        const checkStore = storeEmails.includes(email);
        const checkAccount = accountEmails.includes(email);

        console.log(`User: ${name} | Admin: ${checkAdmin} | Account: ${checkAccount}`);

        // 🔥 Save UID explicitly
        setCurrentUser({ ...user, name: name, uid: user.uid });
        setIsAdmin(checkAdmin);
        setIsHR(checkHR);
        setIsStore(checkStore);
        setIsAccount(checkAccount);

      } else {
        setCurrentUser(null);
        setIsAdmin(false); setIsHR(false); setIsStore(false); setIsAccount(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // 2. DATA LISTENER
  useEffect(() => {
    if (loading || !currentUser) return;

    // 🔥🔥🔥 SMART QUERY FUNCTION (FIXED) 🔥🔥🔥
    const getQuery = (colName, isPublic = false) => {
      const colRef = collection(db, colName);

      // 1. Public Data -> Sabko dikhao
      if (isPublic) return query(colRef);

      // 2. ADMIN -> Sab kuch access
      if (isAdmin) return query(colRef);

      // 3. ACCOUNTANT -> Finance & HR Data
      if (isAccount) {
          const accountAccess = [
              'orders', 'payment_collections', 'payment_dues', 
              'expenses', 'advances', 'leaves', 'attendance', 'travel_notes',
              'card_requests'
          ];
          if (accountAccess.includes(colName)) return query(colRef);
      }

      // 4. HR -> Attendance & Leaves
      if (isHR) {
          const hrAccess = ['attendance', 'leaves', 'advances', 'expenses', 'travel_notes'];
          if (hrAccess.includes(colName)) return query(colRef);
      }

      // 5. STORE -> Inventory
      if (isStore) {
          const storeAccess = ['couriers', 'products', 'orders', 'spare_parts', 'card_requests'];
          if (storeAccess.includes(colName)) return query(colRef);
      }

      // 6. DEFAULT EMPLOYEE -> Sirf Apna Data (Based on ID)
      // 🛑 FIX: Changed 'senderName' to 'senderId' for 100% accuracy
      return query(colRef, where("senderId", "==", currentUser.uid));
    };

    // --- SUBSCRIPTIONS ---
    
    // Public Data (Always Pass 'true')
    const unsubUsers = onSnapshot(getQuery("users", true), (s) => setUsers(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubOrg = onSnapshot(getQuery("organizations", true), (s) => setOrganizations(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubSpare = onSnapshot(getQuery("products", true), (s) => setSpareParts(s.docs.map(d => ({ ...d.data(), id: d.id })))); 
    // 🔥 Fix: Payment Dues ko public/admin ke hisab se nahi, balki 'isPublic=true' kar diya taki sab dekh sakein
    const unsubDues = onSnapshot(getQuery("payment_dues", true), (s) => setDues(s.docs.map(d => ({ ...d.data(), id: d.id })))); 

    // Private Data (Filtered by Role or ID)
    const unsubTasks = onSnapshot(getQuery("tasks"), (s) => setTasks(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubLeads = onSnapshot(getQuery("leads"), (s) => setLeads(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubAtt = onSnapshot(getQuery("attendance"), (s) => setAttendance(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubLeaves = onSnapshot(getQuery("leaves"), (s) => setLeaves(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubExp = onSnapshot(getQuery("expenses"), (s) => setExpenses(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubAdv = onSnapshot(getQuery("advances"), (s) => setAdvances(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubTrav = onSnapshot(getQuery("travel_notes"), (s) => setTravelNotes(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubPMS = onSnapshot(getQuery("pms_reports"), (s) => setPms(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubSales = onSnapshot(getQuery("sales_reports"), (s) => setSalesReports(s.docs.map(d => ({ ...d.data(), id: d.id })))); 
    const unsubOrders = onSnapshot(getQuery("orders"), (s) => setOrders(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubService = onSnapshot(getQuery("service_calls"), (s) => setServiceCalls(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubInstall = onSnapshot(getQuery("installations"), (s) => setInstallations(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubCourier = onSnapshot(getQuery("couriers"), (s) => setCouriers(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubCard = onSnapshot(getQuery("card_requests"), (s) => setCardRequests(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubActivity = onSnapshot(getQuery("activity_plans"), (s) => setActivityPlans(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubPay = onSnapshot(getQuery("payment_collections"), (s) => setPayments(s.docs.map(d => ({ ...d.data(), id: d.id }))));
    const unsubDemos = onSnapshot(getQuery("demos"), (s) => setDemos(s.docs.map(d => ({ ...d.data(), id: d.id }))));

    return () => {
      unsubUsers(); unsubOrg(); unsubSpare(); unsubDues();
      unsubTasks(); unsubLeads(); unsubAtt(); unsubLeaves(); unsubExp(); unsubAdv(); unsubTrav();
      unsubPMS(); unsubSales(); unsubOrders(); unsubService(); unsubInstall(); unsubCourier();
      unsubCard(); unsubActivity(); unsubPay(); unsubDemos();
    };
  }, [currentUser, isAdmin, isAccount, isStore, isHR, loading]);

  // 🔥 Pass Roles in Value
  const value = {
    currentUser, loading,
    isAdmin, isHR, isStore, isAccount, 
    users, 
    tasks, leads, attendance, leaves, expenses, advances, travelNotes,
    pms, salesReports, orders, serviceCalls, installations, couriers,
    cardRequests, activityPlans, spareParts, organizations, payments, dues, demos
  };

  return (
    <WebDataContext.Provider value={value}>
      {!loading && children}
    </WebDataContext.Provider>
  );
};

export const useWebData = () => useContext(WebDataContext);